import React, { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Check, Minus, Plus } from "lucide-react";
import { calculatorAddons, serviceStyles, spirits } from "@/siteData";
import { SectionLabel } from "./SectionLabel";
import { ShoppingListButton } from "./ShoppingListButton";

const DEFAULT_STATE = {
  guestCount: 75,
  durationHours: 3,
  serviceStyle: serviceStyles[0],
  selectedSpirits: ["vodka", "gin", "bourbon"],
  spiritWeights: {},
  selectedAddons: [],
};

export function calculateEvent(state) {
  const totalDrinks = state.guestCount * state.durationHours * 2;
  const liquorDrinks = totalDrinks * 0.5;
  const spiritsPerBottle = 16;
  const weightOf = (id) => Math.max(1, Math.min(3, state.spiritWeights?.[id] || 1));
  const totalWeight = state.selectedSpirits.reduce((sum, id) => sum + weightOf(id), 0) || 1;
  const spiritBottles = state.selectedSpirits.map((spiritId) => ({
    id: spiritId,
    label: spirits.find((spirit) => spirit.id === spiritId)?.label || spiritId,
    weight: weightOf(spiritId),
    bottles: Math.max(1, Math.ceil((liquorDrinks * weightOf(spiritId)) / totalWeight / spiritsPerBottle)),
  }));
  const bartenderCount = Math.ceil(state.guestCount / 50);
  const nonSpiritDrinks = totalDrinks * 0.5;
  const wineBottles = Math.ceil((nonSpiritDrinks * 0.4) / 5);
  const beerUnits = Math.ceil(nonSpiritDrinks * 0.6);
  const extraBartenders = Math.max(bartenderCount - 1, 0);
  const extraHours = Math.max(state.durationHours - 3, 0);
  const addonsTotal = calculatorAddons
    .filter((addon) => state.selectedAddons.includes(addon.id))
    .reduce((sum, addon) => sum + (addon.flat ? addon.cost : addon.cost * state.guestCount), 0);
  const estimatedTotal = 2500 + (extraBartenders * 175) + (extraHours * 100) + addonsTotal;
  return {
    totalDrinks: Math.ceil(totalDrinks),
    spiritBottles,
    wineBottles,
    beerUnits,
    bartenderCount,
    estimatedTotal,
    addonsTotal,
    extraBartenders,
  };
}

export default function EventCalculator({ onApply, onSnapshotChange }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const result = useMemo(() => calculateEvent(state), [state]);

  useEffect(() => {
    onSnapshotChange({ ...state, result });
  }, [state, result, onSnapshotChange]);

  const updateState = (patch) => setState((current) => ({ ...current, ...patch }));
  const toggleSpirit = (id) => setState((current) => {
    const selected = current.selectedSpirits.includes(id);
    const spiritWeights = { ...current.spiritWeights };
    if (selected) delete spiritWeights[id];
    return {
      ...current,
      spiritWeights,
      selectedSpirits: selected
        ? current.selectedSpirits.filter((spirit) => spirit !== id)
        : [...current.selectedSpirits, id],
    };
  });
  const adjustWeight = (id, delta) => setState((current) => ({
    ...current,
    spiritWeights: { ...current.spiritWeights, [id]: Math.max(1, Math.min(3, (current.spiritWeights?.[id] || 1) + delta)) },
  }));
  const toggleAddon = (id) => setState((current) => ({
    ...current,
    selectedAddons: current.selectedAddons.includes(id)
      ? current.selectedAddons.filter((addon) => addon !== id)
      : [...current.selectedAddons, id],
  }));

  return (
    <section className="section calculator-section" id="calculator" data-testid="calculator-widget-container">
      <div className="section-intro reveal-on-scroll">
        <SectionLabel number="06" testId="calculator-section-label">The planning room</SectionLabel>
        <div className="intro-grid">
          <h2 data-testid="calculator-heading">A clearer first conversation.</h2>
          <p data-testid="calculator-description">Set the shape of the night. This planning estimate shows the staffing, spirit quantities, and investment range your brief may require — without the back-and-forth.</p>
        </div>
      </div>

      <div className="calculator-layout">
        <div className="calculator-controls reveal-on-scroll">
          <div className="control-block">
            <div className="control-heading"><div><span className="card-eyebrow">01 / Scale</span><h3>How many will gather?</h3></div><output data-testid="calculator-guest-count-value">{state.guestCount} guests</output></div>
            <input className="range-input" type="range" min="10" max="250" step="5" value={state.guestCount} onChange={(event) => updateState({ guestCount: Number(event.target.value) })} data-testid="calculator-guest-count-slider" aria-label="Number of guests" />
            <div className="range-ends"><span>10</span><span>250</span></div>
          </div>

          <div className="control-block">
            <div className="control-heading"><div><span className="card-eyebrow">02 / Tempo</span><h3>Active service hours</h3></div><output data-testid="calculator-duration-value">{state.durationHours} hours</output></div>
            <input className="range-input" type="range" min="2" max="7" step="1" value={state.durationHours} onChange={(event) => updateState({ durationHours: Number(event.target.value) })} data-testid="calculator-duration-slider" aria-label="Active service hours" />
            <div className="range-ends"><span>2 hrs</span><span>7 hrs</span></div>
          </div>

          <div className="control-block">
            <label className="field-label" htmlFor="service-style"><span className="card-eyebrow">03 / Service</span><span>How should it feel?</span></label>
            <select id="service-style" className="input-control" value={state.serviceStyle} onChange={(event) => updateState({ serviceStyle: event.target.value })} data-testid="calculator-service-style-select">
              {serviceStyles.map((style) => <option key={style}>{style}</option>)}
            </select>
          </div>

          <div className="control-block">
            <div className="field-label"><span className="card-eyebrow">04 / Spirits</span><span>What will you pour?</span></div>
            <p className="field-help">Choose the spirits you plan to purchase. Weight any spirit heavier below and we’ll shift the volume its way.</p>
            <div className="choice-grid spirit-choice-grid">
              {spirits.map((spirit) => {
                const checked = state.selectedSpirits.includes(spirit.id);
                return <label className={`choice-chip${checked ? " is-selected" : ""}`} key={spirit.id}><input type="checkbox" checked={checked} onChange={() => toggleSpirit(spirit.id)} data-testid={`calculator-spirit-${spirit.id}`} /><span>{spirit.label}</span>{checked ? <Check size={14} /> : <Plus size={14} />}</label>;
              })}
            </div>
            {state.selectedSpirits.length > 0 && (
              <div className="pour-emphasis" data-testid="pour-emphasis-panel">
                <span className="pour-emphasis-label">Pour emphasis</span>
                {state.selectedSpirits.map((id) => {
                  const weight = Math.max(1, Math.min(3, state.spiritWeights?.[id] || 1));
                  const label = spirits.find((spirit) => spirit.id === id)?.label || id;
                  return (
                    <div className="pour-emphasis-row" key={id} data-testid={`pour-emphasis-row-${id}`}>
                      <span className="pour-emphasis-name">{label}</span>
                      <div className="pour-emphasis-stepper">
                        <button type="button" onClick={() => adjustWeight(id, -1)} disabled={weight <= 1} aria-label={`Less ${label}`} data-testid={`pour-weight-minus-${id}`}><Minus size={13} /></button>
                        <strong data-testid={`pour-weight-value-${id}`}>×{weight}</strong>
                        <button type="button" onClick={() => adjustWeight(id, 1)} disabled={weight >= 3} aria-label={`More ${label}`} data-testid={`pour-weight-plus-${id}`}><Plus size={13} /></button>
                      </div>
                      <span className="pour-emphasis-tag">{weight === 1 ? "Standard" : weight === 2 ? "Featured" : "Headliner"}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="control-block">
            <div className="field-label"><span className="card-eyebrow">05 / Finish</span><span>Layer in the details</span></div>
            <div className="addon-list">
              {calculatorAddons.map((addon) => {
                const checked = state.selectedAddons.includes(addon.id);
                return <label className={`addon-row${checked ? " is-selected" : ""}`} key={addon.id}><input type="checkbox" checked={checked} onChange={() => toggleAddon(addon.id)} data-testid={`calculator-addon-${addon.id}`} /><span className="addon-check">{checked ? <Check size={14} /> : null}</span><span>{addon.label}</span><strong>{addon.priceLabel}</strong></label>;
              })}
            </div>
          </div>
        </div>

        <aside className="calculator-output reveal-on-scroll" data-testid="calculator-output-panel">
          <div className="output-topline"><span className="card-eyebrow">Live planning estimate</span><span className="pulse-dot" aria-hidden="true" /></div>
          <div className="estimate-wrap"><span className="estimate-label">Starting investment</span><strong data-testid="calculator-estimated-total-price">${result.estimatedTotal.toLocaleString()}</strong><span className="estimate-note">$2,500 custom drink bar package + selected layers · final quote follows a personal review</span></div>
          <div className="output-rule" />
          <div className="output-metric"><span>Recommended staffing</span><strong data-testid="calculator-recommended-bartenders">{result.bartenderCount} bartender{result.bartenderCount === 1 ? "" : "s"}</strong><small>One bartender per 50 guests</small></div>
          <div className="output-rule" />
          <div className="shopping-heading"><span>Spirit shopping guide</span><span>{result.totalDrinks} estimated drinks</span></div>
          <div className="shopping-list" data-testid="calculator-spirit-shopping-list">
            {result.spiritBottles.length ? result.spiritBottles.map((spirit) => <div className="shopping-row" key={spirit.id} data-testid={`calculator-spirit-count-${spirit.id}`}><span>{spirit.label}{spirit.weight > 1 ? <em className="shopping-weight"> ×{spirit.weight}</em> : null}</span><strong>{spirit.bottles} <small>750ml bottles</small></strong></div>) : <div className="shopping-empty">Select at least one spirit to build your list.</div>}
            <div className="shopping-row shopping-row-extra" data-testid="calculator-wine-count"><span>Wine</span><strong>{result.wineBottles} <small>750ml bottles</small></strong></div>
            <div className="shopping-row shopping-row-extra" data-testid="calculator-beer-count"><span>Beer</span><strong>{result.beerUnits} <small>12oz bottles/cans</small></strong></div>
          </div>
          <p className="formula-note">Estimate assumes 2 drinks per guest per active hour — half of the volume allocated to spirits, the rest split between beer (60%) and wine (40%). One 750ml spirit bottle yields ~16 cocktails; a wine bottle pours 5 glasses.</p>
          <ShoppingListButton state={state} result={result} />
          <button type="button" className="button button-primary full-button" data-testid="calculator-apply-to-quote-button" onClick={() => onApply({ ...state, result })}>Carry this into my quote <ArrowUpRight size={16} /></button>
          <div className="output-footer"><Minus size={14} /> Built for a better first conversation</div>
        </aside>
      </div>
    </section>
  );
}
